import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthContext';
import { Button } from '../components/ui';

interface LocationState {
  from?: { pathname: string };
}

/** Login mounted at /login. */
export const LoginPage: React.FC = () => {
  const { login, isLoggingIn, loginError, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const state = location.state as LocationState | undefined;
  const returnTo = state?.from?.pathname ?? '/';

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate(returnTo, { replace: true });
    }
  }, [isAuthenticated, navigate, returnTo]);

  const emailInvalid = submitted && email.trim() === '';
  const passwordInvalid = submitted && password === '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (email.trim() === '' || password === '') return;
    try {
      await login(email.trim(), password);
      navigate(returnTo, { replace: true });
    } catch {
      /* surfaced via loginError */
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 px-4"
    >
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <div
            aria-hidden
            className="inline-flex h-12 w-12 items-center justify-center rounded-xl
                       bg-gradient-to-br from-primary-400 to-primary-600 text-white
                       text-xl font-bold shadow-card mb-3"
          >
            ⚒
          </div>
          <h1 className="text-2xl font-semibold text-slate-900">Welcome back</h1>
          <p className="text-sm text-slate-600 mt-1">Sign in to your Forge workspace.</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white border border-slate-200 rounded-xl shadow-card p-6 space-y-4"
          aria-label="Sign in to WebPortal"
          noValidate
        >
          <Field
            id="email"
            label="Email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={setEmail}
            invalid={emailInvalid}
            invalidMessage="Email is required"
            disabled={isLoggingIn}
          />
          <Field
            id="password"
            label="Password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={setPassword}
            invalid={passwordInvalid}
            invalidMessage="Password is required"
            disabled={isLoggingIn}
          />

          {loginError && (
            <div
              role="alert"
              className="text-xs text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-3 py-2"
            >
              {loginError}
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={isLoggingIn}
            className="w-full"
          >
            {isLoggingIn ? 'Signing in…' : 'Sign in'}
          </Button>

          <p className="text-xs text-slate-600 text-center pt-3 border-t border-slate-200">
            Don't have an account?{' '}
            <Link to="/signup" className="text-primary-500 hover:text-primary-600 font-medium">
              Create one
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

const Field: React.FC<{
  id: string;
  label: string;
  type: string;
  autoComplete: string;
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
  invalidMessage?: string;
  disabled?: boolean;
}> = ({ id, label, type, autoComplete, value, onChange, invalid, invalidMessage, disabled }) => (
  <div className="space-y-1">
    <label htmlFor={id} className="block text-xs font-medium text-slate-700">
      {label}
    </label>
    <input
      id={id}
      name={id}
      type={type}
      autoComplete={autoComplete}
      required
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-invalid={invalid || undefined}
      aria-describedby={invalid ? `${id}-error` : undefined}
      disabled={disabled}
      className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm
                 placeholder:text-slate-400 disabled:bg-slate-50 disabled:text-slate-500"
    />
    {invalid && invalidMessage && (
      <p id={`${id}-error`} className="text-xs text-danger-700">
        {invalidMessage}
      </p>
    )}
  </div>
);

export default LoginPage;
